// src/frontend/src/pages/Settings.tsx - Enhanced with dedicated billing section
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';

interface EmailPreferences {
  lowCreditsWarning: boolean;
  monthlyProgress: boolean;
  simulationComplete: boolean;
  trialEnding: boolean;
  planExpiration: boolean;
  marketingEmails: boolean;
}

interface UserPlan {
  planType: string;
  creditsUsed: number;
  creditsTotal: number;
  isTrialActive: boolean;
  trialDaysLeft: number;
  nextBillingDate?: string;
  subscriptionStatus?: string;
}

const Settings: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'profile' | 'billing' | 'email' | 'account'>('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [userPlan, setUserPlan] = useState<UserPlan | null>(null);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);

  // Profile state
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    organization: user?.organization || ''
  });

  // Email preferences state
  const [emailPreferences, setEmailPreferences] = useState<EmailPreferences>({
    lowCreditsWarning: true,
    monthlyProgress: true,
    simulationComplete: false,
    trialEnding: true,
    planExpiration: true,
    marketingEmails: false
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchEmailPreferences();
    fetchUserPlan();
  }, []);

  const fetchUserPlan = async () => {
    try {
      const response = await api.get('/users/plan');
      setUserPlan(response.data.data);
    } catch (err) {
      console.error('Failed to fetch user plan:', err);
    }
  };

  const fetchEmailPreferences = async () => {
    try {
      const response = await api.get('/users/email-preferences');
      setEmailPreferences(response.data.data);
    } catch (err) {
      console.error('Failed to fetch email preferences:', err);
    }
  };

  const createCheckoutSession = async (planType: string, billingPeriod: string) => {
    setIsCreatingCheckout(true);
    try {
      const response = await api.post('/payments/create-checkout-session', {
        planType,
        billingPeriod
      });
      
      if (response.data.data.url) {
        window.location.href = response.data.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      setError('Failed to start upgrade. Please try again.');
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsLoading(true);

    try {
      await api.put('/users/profile', profileData);
      setSuccessMessage('Profile updated successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailPreferencesUpdate = async () => {
    setError('');
    setSuccessMessage('');
    setIsLoading(true);

    try {
      await api.put('/users/email-preferences', emailPreferences);
      setSuccessMessage('Email preferences updated successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update email preferences');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      await api.post('/users/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      setSuccessMessage('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  const getPlanDisplayName = (planType: string) => {
    switch (planType) {
      case 'solo_core': return 'Solo Core';
      case 'solo_pro': return 'Solo Pro';
      case 'team_pilot': return 'Team Pilot';
      case 'free_trial': return 'Free Trial';
      default: return planType;
    }
  };

  const getPlanPrice = (planType: string) => {
    switch (planType) {
      case 'solo_core': return '$19/month';
      case 'solo_pro': return '$39/month';
      case 'team_pilot': return 'Custom pricing';
      case 'free_trial': return 'Free';
      default: return 'N/A';
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Settings</h1>
          
          {/* PERSISTENT UPGRADE BUTTON - Always visible */}
          {userPlan && (userPlan.isTrialActive || userPlan.planType === 'solo_core') && (
            <div className="flex items-center gap-3">
              {userPlan.isTrialActive && (
                <div className="text-right">
                  <div className="text-sm font-medium text-orange-600">
                    Trial expires in {userPlan.trialDaysLeft} days
                  </div>
                  <div className="text-xs text-gray-500">
                    {userPlan.creditsTotal - userPlan.creditsUsed} sessions remaining
                  </div>
                </div>
              )}
              <button
                onClick={() => navigate('/pricing')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium flex items-center shadow-lg"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                {userPlan.planType === 'solo_core' ? 'Upgrade to Pro' : 'Upgrade Now'}
              </button>
            </div>
          )}
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {successMessage}
          </div>
        )}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-2 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'profile'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Profile
              </button>
              <button
                onClick={() => setActiveTab('billing')}
                className={`py-2 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'billing'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <span className="flex items-center">
                  Billing & Plans
                  {userPlan?.isTrialActive && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      Trial
                    </span>
                  )}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('email')}
                className={`py-2 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'email'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Email Notifications
              </button>
              <button
                onClick={() => setActiveTab('account')}
                className={`py-2 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'account'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Account Security
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <form onSubmit={handleProfileUpdate}>
                <div className="mb-4">
                  <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring focus:border-blue-300"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring focus:border-blue-300"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="organization" className="block text-gray-700 font-medium mb-2">
                    Organization
                  </label>
                  <input
                    type="text"
                    id="organization"
                    value={profileData.organization}
                    onChange={(e) => setProfileData({ ...profileData, organization: e.target.value })}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring focus:border-blue-300"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className={`bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 ${
                    isLoading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isLoading ? 'Saving...' : 'Save Profile'}
                </button>
              </form>
            )}

            {/* NEW: Billing & Plans Tab */}
            {activeTab === 'billing' && (
              <div>
                <h3 className="text-lg font-medium mb-6">Billing & Subscription</h3>
                
                {userPlan && (
                  <div className="space-y-6">
                    {/* Current Plan Card */}
                    <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900">
                            Current Plan: {getPlanDisplayName(userPlan.planType)}
                          </h4>
                          <p className="text-gray-600">{getPlanPrice(userPlan.planType)}</p>
                        </div>
                        <div className="text-right">
                          {userPlan.isTrialActive ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                              Free Trial • {userPlan.trialDaysLeft} days left
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Usage Stats */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <div className="text-sm text-gray-600 mb-1">Sessions Used</div>
                          <div className="text-2xl font-bold text-gray-900">
                            {userPlan.creditsUsed} / {userPlan.creditsTotal}
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                              style={{ width: `${(userPlan.creditsUsed / userPlan.creditsTotal) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">Sessions Remaining</div>
                          <div className="text-2xl font-bold text-green-600">
                            {userPlan.creditsTotal - userPlan.creditsUsed}
                          </div>
                          <div className="text-sm text-gray-500 mt-2">
                            Resets {userPlan.nextBillingDate ? 'on ' + new Date(userPlan.nextBillingDate).toLocaleDateString() : 'monthly'}
                          </div>
                        </div>
                      </div>

                      {/* Trial Expiration Warning */}
                      {userPlan.isTrialActive && userPlan.trialDaysLeft <= 7 && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                          <div className="flex items-start">
                            <svg className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <div>
                              <h5 className="text-red-800 font-medium">
                                {userPlan.trialDaysLeft === 1 ? 'Trial expires tomorrow!' : `Trial expires in ${userPlan.trialDaysLeft} days`}
                              </h5>
                              <p className="text-red-700 text-sm mt-1">
                                Subscribe now to continue your PM coaching journey without interruption.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Upgrade Options */}
                    {(userPlan.isTrialActive || userPlan.planType === 'solo_core') && (
                      <div className="border border-blue-200 rounded-lg p-6 bg-blue-50">
                        <h4 className="text-lg font-semibold text-blue-900 mb-4">
                          {userPlan.planType === 'solo_core' ? 'Upgrade to Pro' : 'Choose Your Plan'}
                        </h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                          {userPlan.planType !== 'solo_core' && (
                            <div className="border border-gray-200 rounded-lg p-4 bg-white">
                              <h5 className="font-medium text-gray-900 mb-2">Solo Core</h5>
                              <div className="text-2xl font-bold text-gray-900 mb-1">$19<span className="text-base font-normal text-gray-600">/month</span></div>
                              <ul className="text-sm text-gray-600 space-y-1 mb-4">
                                <li>• 20 coaching sessions/month</li>
                                <li>• All persona packs</li>
                                <li>• PDF feedback reports</li>
                                <li>• Email support</li>
                              </ul>
                              <button
                                onClick={() => createCheckoutSession('solo_core', 'monthly')}
                                disabled={isCreatingCheckout}
                                className="w-full bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50"
                              >
                                {isCreatingCheckout ? 'Setting up...' : 'Choose Solo Core'}
                              </button>
                            </div>
                          )}
                          
                          <div className="border-2 border-blue-500 rounded-lg p-4 bg-white relative">
                            {userPlan.planType === 'solo_core' && (
                              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                                  Recommended Upgrade
                                </span>
                              </div>
                            )}
                            <h5 className="font-medium text-gray-900 mb-2">Solo Pro</h5>
                            <div className="text-2xl font-bold text-blue-600 mb-1">$39<span className="text-base font-normal text-gray-600">/month</span></div>
                            <ul className="text-sm text-gray-600 space-y-1 mb-4">
                              <li>• <strong>60 coaching sessions/month</strong></li>
                              <li>• <strong>Real-time coaching tips</strong></li>
                              <li>• Custom scenario creator</li>
                              <li>• Priority email support</li>
                              <li>• Everything in Solo Core</li>
                            </ul>
                            <button
                              onClick={() => createCheckoutSession('solo_pro', 'monthly')}
                              disabled={isCreatingCheckout}
                              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                              {isCreatingCheckout ? 'Setting up...' : 
                               userPlan.planType === 'solo_core' ? 'Upgrade to Pro' : 'Choose Solo Pro'}
                            </button>
                          </div>
                        </div>

                        <div className="text-center">
                          <Link 
                            to="/pricing" 
                            className="text-blue-600 hover:text-blue-800 text-sm underline"
                          >
                            View all pricing options →
                          </Link>
                        </div>
                      </div>
                    )}

                    {/* Need Team Plan? */}
                    <div className="border border-purple-200 rounded-lg p-6 bg-purple-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-lg font-semibold text-purple-900">Need a Team Plan?</h4>
                          <p className="text-purple-700 text-sm mt-1">
                            Get custom pricing for 10+ seats with team analytics and dedicated support.
                          </p>
                        </div>
                        <Link
                          to="/contact?plan=team-pilot"
                          className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors whitespace-nowrap"
                        >
                          Contact Sales
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Email Notifications Tab */}
            {activeTab === 'email' && (
              <div>
                <h3 className="text-lg font-medium mb-4">Email Notification Preferences</h3>
                <p className="text-gray-600 mb-6">
                  Choose which email notifications you'd like to receive.
                </p>

                <div className="space-y-4">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={emailPreferences.lowCreditsWarning}
                      onChange={(e) => 
                        setEmailPreferences({ ...emailPreferences, lowCreditsWarning: e.target.checked })
                      }
                      className="mt-1 mr-3"
                    />
                    <div>
                      <span className="font-medium">Low Credits Warning</span>
                      <p className="text-sm text-gray-600">
                        Get notified when your coaching sessions drop below 20%
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={emailPreferences.monthlyProgress}
                      onChange={(e) => 
                        setEmailPreferences({ ...emailPreferences, monthlyProgress: e.target.checked })
                      }
                      className="mt-1 mr-3"
                    />
                    <div>
                      <span className="font-medium">Monthly Progress Reports</span>
                      <p className="text-sm text-gray-600">
                        Receive monthly summaries of your simulation performance
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={emailPreferences.simulationComplete}
                      onChange={(e) => 
                        setEmailPreferences({ ...emailPreferences, simulationComplete: e.target.checked })
                      }
                      className="mt-1 mr-3"
                    />
                    <div>
                      <span className="font-medium">Simulation Completion</span>
                      <p className="text-sm text-gray-600">
                        Get notified when your simulation feedback is ready
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={emailPreferences.trialEnding}
                      onChange={(e) => 
                        setEmailPreferences({ ...emailPreferences, trialEnding: e.target.checked })
                      }
                      className="mt-1 mr-3"
                    />
                    <div>
                      <span className="font-medium">Trial Ending Reminders</span>
                      <p className="text-sm text-gray-600">
                        Receive reminders before your free trial expires
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={emailPreferences.planExpiration}
                      onChange={(e) => 
                        setEmailPreferences({ ...emailPreferences, planExpiration: e.target.checked })
                      }
                      className="mt-1 mr-3"
                    />
                    <div>
                      <span className="font-medium">Plan Expiration Notices</span>
                      <p className="text-sm text-gray-600">
                        Get notified before your subscription expires
                      </p>
                    </div>
                  </label>

                  <div className="border-t pt-4 mt-6">
                    <label className="flex items-start">
                      <input
                        type="checkbox"
                        checked={emailPreferences.marketingEmails}
                        onChange={(e) => 
                          setEmailPreferences({ ...emailPreferences, marketingEmails: e.target.checked })
                        }
                        className="mt-1 mr-3"
                      />
                      <div>
                        <span className="font-medium">Product Updates & Tips</span>
                        <p className="text-sm text-gray-600">
                          Receive occasional emails about new features and PM best practices
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                <button
                  onClick={handleEmailPreferencesUpdate}
                  disabled={isLoading}
                  className={`mt-6 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 ${
                    isLoading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isLoading ? 'Saving...' : 'Save Preferences'}
                </button>
              </div>
            )}

            {/* Account Security Tab */}
            {activeTab === 'account' && (
              <form onSubmit={handlePasswordChange}>
                <h3 className="text-lg font-medium mb-4">Change Password</h3>

                <div className="mb-4">
                  <label htmlFor="currentPassword" className="block text-gray-700 font-medium mb-2">
                    Current Password
                  </label>
                  <input
                    type="password"
                    id="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={(e) => 
                      setPasswordData({ ...passwordData, currentPassword: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring focus:border-blue-300"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="newPassword" className="block text-gray-700 font-medium mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    value={passwordData.newPassword}
                    onChange={(e) => 
                      setPasswordData({ ...passwordData, newPassword: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring focus:border-blue-300"
                    required
                    minLength={8}
                  />
                  <p className="text-sm text-gray-600 mt-1">
                    Must be at least 8 characters long
                  </p>
                </div>

                <div className="mb-6">
                  <label htmlFor="confirmPassword" className="block text-gray-700 font-medium mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={(e) => 
                      setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring focus:border-blue-300"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className={`bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 ${
                    isLoading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isLoading ? 'Updating...' : 'Change Password'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Settings;